/**
 * @fileoverview Own reusable explorer interaction around caller-owned document/folder data. Local preferences, selection, and contextual actions connect to `documentClipboard.js`, `FileContextMenu.js`, and `FolderContextMenu.js`. Parents own canonical location and mutation callbacks; this controller coordinates presentation without making browser folder state authoritative.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  consumeDocumentCut,
  decodeDocumentClipboard,
  encodeDocumentClipboard,
  isClipboardEditingTarget,
  readDocumentClipboard,
} from './documentClipboard';
import {
  fileContextMenuState,
  isFileContextMenuKey,
} from './FileContextMenu';
import {
  folderContextMenuState,
  folderCreateContextMenuState,
} from './FolderContextMenu';
import { documentFileType } from './DocumentFileTypeIcon';



export function shouldUseDocumentFileViewer(document) {
  const type = documentFileType(document);
  // Unknown legacy Simul files are overwhelmingly Word documents whose list
  // metadata predates MIME/type hydration. Only affirmatively non-Word file
  // types should be diverted away from the document workspace.
  return type !== 'word' && type !== 'file';
}



export function normalizeDocumentFolderPath(value) {
  return String(value || '')
    .replaceAll('\\', '/')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && part !== '.' && part !== '..')
    .join('/');
}



export function documentFolderName(folder, fallback = 'Folder') {
  if (folder?.name) return folder.name;
  const parts = normalizeDocumentFolderPath(folder?.folder_path).split('/').filter(Boolean);
  return parts[parts.length - 1] || fallback;
}



export function documentFolderCount(folder, key, fallbackKey = '') {
  const raw = folder?.[key] ?? (fallbackKey ? folder?.[fallbackKey] : 0);
  const count = Number(raw || 0);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}



export function documentClipboardSelection(type, item) {
  const itemId = type === 'folder' ? item?.folder_id : item?.document_id;
  if (!itemId) return null;
  return {
    mode: 'items',
    items: [{
      item_type: type === 'folder' ? 'folder' : 'document',
      item_id: itemId,
      ...(item?.updated_at ? { expected_updated_at: item.updated_at } : {}),
    }],
  };
}



export function useExplorerContextMenus() {
  const [fileContextMenu, setFileContextMenu] = useState(null);
  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [folderCreateContextMenu, setFolderCreateContextMenu] = useState(null);

  const closeContextMenus = useCallback(

  () => {
    setFileContextMenu(null);
    setFolderContextMenu(null);
    setFolderCreateContextMenu(null);
  }, []);

  const openFileContextMenu = useCallback(

  (event, document) => {
    setFolderContextMenu(null);
    setFolderCreateContextMenu(null);
    setFileContextMenu(fileContextMenuState(event, document));
  }, []);

  const openFolderContextMenu = useCallback(

  (event, folder) => {
    setFileContextMenu(null);
    setFolderCreateContextMenu(null);
    setFolderContextMenu(folderContextMenuState(event, folder));
  }, []);

  const openFolderCreateContextMenu = useCallback(

  (event, folder, canCreateFolder = true) => {
    setFileContextMenu(null);
    setFolderContextMenu(null);
    setFolderCreateContextMenu(folderCreateContextMenuState(event, folder, canCreateFolder));
  }, []);

  const openFileContextMenuFromKey = useCallback(

  (event, document) => {
    if (!isFileContextMenuKey(event)) return false;
    openFileContextMenu(event, document);
    return true;
  }, [openFileContextMenu]);

  const openFolderContextMenuFromKey = useCallback(

  (event, folder) => {
    if (!isFileContextMenuKey(event)) return false;
    openFolderContextMenu(event, folder);
    return true;
  }, [openFolderContextMenu]);
  const closeFileContextMenu = useCallback(

  () => setFileContextMenu(null), []);
  const closeFolderContextMenu = useCallback(

  () => setFolderContextMenu(null), []);
  const closeFolderCreateContextMenu = useCallback(

  () => setFolderCreateContextMenu(null), []);

  return {
    fileContextMenu,
    folderContextMenu,
    folderCreateContextMenu,
    openFileContextMenu,
    openFolderContextMenu,
    openFolderCreateContextMenu,
    openFileContextMenuFromKey,
    openFolderContextMenuFromKey,
    closeFileContextMenu,
    closeFolderContextMenu,
    closeFolderCreateContextMenu,
    closeContextMenus,
  };
}



export function useDocumentExplorerSelection({
  documents = [],
  loading = false,
  locationKey = '',
  onOpenDocument,
  shouldUseViewer = shouldUseDocumentFileViewer,
} = {}) {
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState(null);
  const [fileViewerDocument, setFileViewerDocument] = useState(null);

  useEffect(() => {
    setSelectedPreviewDocument(null);
  }, [locationKey]);

  useEffect(

  () => {
    if (!selectedPreviewDocument || loading) return;
    const refreshed = documents.find((document) => (
      String(document.document_id || document.id || '') ===
      String(selectedPreviewDocument.document_id || selectedPreviewDocument.id || '')
    ));
    if (!refreshed) setSelectedPreviewDocument(null);
    else if (refreshed !== selectedPreviewDocument) setSelectedPreviewDocument(refreshed);
  }, [documents, loading, selectedPreviewDocument]);

  const openDocument = useCallback(

  (document) => {
    if (!document) return;
    if (shouldUseViewer?.(document)) setFileViewerDocument(document);
    else if (onOpenDocument) onOpenDocument(document);
  }, [onOpenDocument, shouldUseViewer]);
  const clearPreviewDocument = useCallback(

  () => setSelectedPreviewDocument(null), []);
  const closeFileViewer = useCallback(

  () => setFileViewerDocument(null), []);

  return {
    selectedPreviewDocument,
    selectPreviewDocument: setSelectedPreviewDocument,
    clearPreviewDocument,
    openDocument,
    fileViewerDocument,
    closeFileViewer,
  };
}



export function useDocumentClipboardController({
  activeFolder = null,
  onPaste,
  onRemember,
  onRememberError,
  onPasteFiles,
  onPasteComplete,
  onPasteError,
  onMissingDestination,
  keyboard = true,
} = {}) {
  const [clipboard, setClipboard] = useState(null);
  const pasteInProgress = useRef(false);

  const rememberSelection = useCallback(

  async (action, selection, item = null, type = '') => {
    if (!selection) return;
    try {
      const text = encodeDocumentClipboard(action, selection);
      await navigator.clipboard.writeText(text);
      const next = { ...decodeDocumentClipboard(text), item, type };
      setClipboard(next);
      onRemember?.(next);
    } catch (error) {
      setClipboard(null);
      onRememberError?.(error);
    }
  }, [onRemember, onRememberError]);

  const rememberItem = useCallback(

  (action, type, item) => {
    rememberSelection(action, documentClipboardSelection(type, item), item, type);
  }, [rememberSelection]);

  const pasteIntoFolder = useCallback(

  async (destination = activeFolder, pasted = undefined) => {
    if (pasteInProgress.current) return null;
    if (!destination) {
      onMissingDestination?.();
      return null;
    }
    pasteInProgress.current = true;
    try {
      const source = pasted === undefined ? await readDocumentClipboard() : pasted;
      if (source?.files?.length) return await onPasteFiles?.(destination, { files: source.files });
      if (!source) throw new Error('Copy a document first, or use Ctrl+V or ⌘V to paste files from your computer.');
      const result = await onPaste?.(source, destination);
      if (source.action === 'move' && result && !result.results?.some(item => item.status === 'failed')) {
        consumeDocumentCut(source);
        setClipboard(null);
      }
      onPasteComplete?.(source, destination, result);
      return result;
    } catch (error) {
      onPasteError?.(error, clipboard, destination);
      return null;
    } finally {
      pasteInProgress.current = false;
    }
  }, [activeFolder, clipboard, onMissingDestination, onPaste, onPasteFiles, onPasteComplete, onPasteError]);

  useEffect(

  () => {
    if (!keyboard) return undefined;
    const handlePaste =

    (event) => {
      if (event.defaultPrevented || isClipboardEditingTarget(event.target)) return;
      const files = Array.from(event.clipboardData?.files || []);
      const source = files.length && onPasteFiles
        ? { files }
        : decodeDocumentClipboard(event.clipboardData?.getData('text/plain'));
      if (!source) return;
      event.preventDefault();
      pasteIntoFolder(activeFolder, source);
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [activeFolder, keyboard, onPasteFiles, pasteIntoFolder]);
  const clearClipboard = useCallback(

  () => setClipboard(null), []);
  const copyDocument = useCallback(

  (document) => rememberItem('copy', 'file', document), [rememberItem]);
  const cutDocument = useCallback(

  (document) => rememberItem('move', 'file', document), [rememberItem]);
  const copyFolder = useCallback(

  (folder) => rememberItem('copy', 'folder', folder), [rememberItem]);
  const cutFolder = useCallback(

  (folder) => rememberItem('move', 'folder', folder), [rememberItem]);

  return {
    clipboard,
    canPaste: Boolean(navigator.clipboard?.readText),
    clearClipboard,
    rememberSelection,
    rememberItem,
    copyDocument,
    cutDocument,
    copyFolder,
    cutFolder,
    pasteIntoFolder,
  };
}



export default function useDocumentFileExplorerController({
  selection,
  clipboard,
} = {}) {
  const selectionController = useDocumentExplorerSelection(selection);
  const contextMenuController = useExplorerContextMenus();
  const clipboardController = useDocumentClipboardController(clipboard);
  return {
    ...selectionController,
    ...contextMenuController,
    ...clipboardController,
  };
}
